import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { BoardPanel } from '../board/BoardPanel';
import { animate, displayStyle, ease, prefersReducedMotion } from '../board/ui';
import { glowTexture } from '../road/glow';

/** The flash as plain data, for browser tests. */
export interface NameFlashSnapshot {
  visible: boolean;
  text: string;
  /** The plate is the slot's own reel floor. */
  floored: boolean;
}

const IN_MS = 220;
const HOLD_MS = 900;
const OUT_MS = 380;

/**
 * The slot's name as it opens: a plate with the name in gold pops up over
 * the reels, a light sweeps across it, it holds a moment and fades. The
 * plate is the slot's own reel floor, faded under its shade with a brass
 * edge, as the slot's other boards (producer 2026-10-19: it matches the
 * land, not a wooden plate). Drawn on the slot's canvas. It takes no taps.
 */
export class NameFlash extends Container {
  private readonly glow = new Sprite(glowTexture());
  private readonly card = new Container();
  private plate: BoardPanel | null = null;
  private readonly title: Text;
  private readonly streak = new Sprite(glowTexture());
  private readonly streakMask = new Graphics();
  private readonly sparks: Sprite[] = [];
  private maxWidth = 360;
  private run = 0;

  /** `floor`: the slot's reel floor, read each time the flash plays (null
   *  until it has loaded: the plate is then the boards' plain dark base). */
  constructor(private readonly floor: () => Texture | null = () => null) {
    super();
    this.glow.anchor.set(0.5);
    this.glow.tint = 0xffc864;
    this.title = new Text({ text: '', style: displayStyle(34) });
    this.title.anchor.set(0.5);
    this.streak.anchor.set(0.5);
    this.streak.blendMode = 'add';
    this.streak.tint = 0xfff1c0;
    this.streak.mask = this.streakMask;
    for (let i = 0; i < 8; i++) {
      const s = new Sprite(glowTexture());
      s.anchor.set(0.5);
      s.blendMode = 'add';
      s.tint = i % 2 ? 0xffd36b : 0xffffff;
      this.sparks.push(s);
    }
    this.card.addChild(this.title, this.streak, this.streakMask);
    this.addChild(this.glow, ...this.sparks, this.card);
    this.eventMode = 'none';
    this.visible = false;
  }

  get snapshot(): NameFlashSnapshot {
    // A flash taken down with its scene mid-play is gone.
    if (this.destroyed) return { visible: false, text: '', floored: false };
    const floor = this.plate?.floorTexture ?? null;
    return { visible: this.visible, text: this.title.text, floored: !!floor && floor === this.floor() };
  }

  /** Take the flash down at once: a card or bonus has covered the slot. */
  hide(): void {
    this.run += 1;
    if (!this.destroyed) this.visible = false;
  }

  /** Centre the flash on (x, y), no wider than `maxWidth`. */
  layout(x: number, y: number, maxWidth: number): void {
    this.maxWidth = maxWidth;
    this.position.set(Math.round(x), Math.round(y));
  }

  /** Show `name`; resolves when it has gone. */
  async play(name: string): Promise<void> {
    const run = ++this.run;
    const text = name.toUpperCase();
    // Bigger on a big screen, never below the phone size.
    let size = Math.round(Math.max(34, Math.min(48, this.maxWidth * 0.085)));
    this.title.style = displayStyle(size);
    this.title.text = text;
    const padX = 46;
    const room = this.maxWidth - padX * 2;
    if (this.title.width > room) {
      size = Math.max(20, Math.floor((size * room) / this.title.width));
      this.title.style = displayStyle(size);
    }
    const w = Math.round(this.title.width + padX * 2);
    const h = Math.round(size * 2);
    this.drawPlate(w, h);
    this.glow.width = w * 1.5;
    this.glow.height = h * 3;
    this.streakMask.clear().roundRect(-w / 2 + 10, -h / 2 + 6, w - 20, h - 12, h / 2).fill({ color: 0xffffff });
    this.streak.width = h * 1.1;
    this.streak.height = h * 1.6;
    this.streak.alpha = 0;
    this.visible = true;
    this.alpha = 1;
    this.sparks.forEach((s) => (s.alpha = 0));

    if (prefersReducedMotion()) {
      this.card.scale.set(1);
      this.glow.alpha = 0.5;
      await new Promise((r) => setTimeout(r, IN_MS + HOLD_MS));
      if (run === this.run && !this.destroyed) this.visible = false;
      return;
    }

    const angles = this.sparks.map((_, i) => (i / this.sparks.length) * Math.PI * 2 + Math.random() * 0.5);
    await animate(IN_MS, (t) => {
      const e = ease.outBack(t);
      this.alpha = Math.min(1, t * 2);
      this.card.scale.set(0.82 + 0.18 * e);
      this.glow.alpha = 0.55 * t;
    });
    if (run !== this.run || this.destroyed) return;
    // Sparks burst from behind the plate while a light sweeps across it.
    void animate(520, (t) => {
      this.sparks.forEach((s, i) => {
        const r = (w * 0.35 + w * 0.3 * ease.outCubic(t)) * (0.7 + (i % 3) * 0.15);
        s.position.set(Math.cos(angles[i]!) * r, Math.sin(angles[i]!) * r * 0.5);
        s.alpha = t < 0.25 ? t / 0.25 : 1 - (t - 0.25) / 0.75;
        s.scale.set(0.2 + 0.15 * (i % 3));
      });
    });
    void animate(620, (t) => {
      this.streak.alpha = Math.sin(t * Math.PI) * 0.85;
      this.streak.x = -w / 2 + w * t;
    });
    await new Promise((r) => setTimeout(r, HOLD_MS));
    if (run !== this.run || this.destroyed) return;
    await animate(OUT_MS, (t) => {
      this.alpha = 1 - t;
      this.card.scale.set(1 + 0.05 * t);
    });
    if (run === this.run && !this.destroyed) this.visible = false;
  }

  /** The slot's floor as a pill, the way its boards draw it. */
  private drawPlate(w: number, h: number): void {
    if (this.plate) {
      this.card.removeChild(this.plate);
      this.plate.destroy({ children: true });
    }
    const plate = new BoardPanel({ floor: this.floor(), radius: Math.round(h / 2) });
    plate.resize(w, h);
    plate.position.set(-w / 2, -h / 2);
    this.plate = plate;
    this.card.addChildAt(plate, 0);
    this.title.position.set(0, 2);
  }
}
